import { useQueries } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaRegCopy } from "react-icons/fa6";

import type { ChangeStorageTierParams } from "@/apiClient/models/ChangeStorageTierParams";
import type { DataAssetSearchParameters } from "@/apiClient/models/DataAssetSearchParameters";
import useApiClient from "@/hooks/useApiClient";
import { QUERY_OPTIONS_LOAD_ONCE } from "@/apiUtils";
import { manifestLabel } from "@/components/folderConfig/folderConfig.utils";

const MAX_NAMES_SHOWN = 3;

/**
 * Resolves the manifest/deployment/data-set public IDs referenced by a job's
 * search params to display names, using the same react-query cache keys as
 * the request panel's SmartFilterChips (`manifestMetadata`,
 * `realAssetDeploymentDetail`, `dataSetDetail`). Shared keys mean a 50-job
 * list referencing the same entities resolves each one exactly once.
 */
function useEntityNames(search: DataAssetSearchParameters) {
  const api = useApiClient();

  const descriptors = [
    ...(search.manifest_public_ids ?? []).map((publicId) => ({
      publicId,
      queryKey: ["manifestMetadata", publicId],
      queryFn: () => api.dataTransfer.getManifestMetadata({ publicId }),
    })),
    ...(search.real_asset_deployment_public_ids ?? []).map((publicId) => ({
      publicId,
      queryKey: ["realAssetDeploymentDetail", publicId],
      queryFn: () => api.dataAccess.realAssetDeploymentDetail({ publicId }),
    })),
    ...(search.data_set_public_ids ?? []).map((publicId) => ({
      publicId,
      queryKey: ["dataSetDetail", publicId],
      queryFn: () => api.dataAccess.dataSetDetail({ publicId }),
    })),
  ];

  const results = useQueries({
    queries: descriptors.map(({ queryKey, queryFn }) => ({
      queryKey,
      queryFn,
      ...QUERY_OPTIONS_LOAD_ONCE,
      // Old jobs can reference since-deleted entities; fall back to the raw
      // ID instead of blowing up the page.
      throwOnError: false,
    })),
  });

  const nameByPublicId = new Map<string, string>();
  results.forEach((result, index) => {
    const name = (result.data as { name?: string | null } | undefined)?.name;
    if (name) {
      nameByPublicId.set(descriptors[index].publicId, name);
    }
  });
  return nameByPublicId;
}

const summarizeList = (labels: string[], noun: string): string | null => {
  if (labels.length === 0) {
    return null;
  }
  const shown = labels.slice(0, MAX_NAMES_SHOWN).join(", ");
  const extra =
    labels.length > MAX_NAMES_SHOWN ? ` +${labels.length - MAX_NAMES_SHOWN}` : "";
  const count =
    labels.length === 1 ? noun : `${labels.length} ${noun}s`;
  return `${count} ${shown}${extra}`;
};

/**
 * Builds the human-readable scope parts for a tier-change job, e.g.
 * `["→ cool", "2 manifests A, B", "deployment Hercules 42", "path \"Stills\""]`.
 */
const buildSummaryParts = (
  params: Partial<ChangeStorageTierParams> | null | undefined,
  nameByPublicId: Map<string, string>
): string[] => {
  if (!params?.target_tier && !params?.search) {
    return ["Tier change"];
  }

  const search = params.search ?? {};
  const resolveNames = (ids?: Array<string>) =>
    (ids ?? []).map((publicId) => nameByPublicId.get(publicId) ?? publicId);

  const parts: (string | null)[] = [
    params.target_tier ? `→ ${params.target_tier}` : null,
    summarizeList(resolveNames(search.manifest_public_ids), "manifest"),
    summarizeList((search.manifest_types ?? []).map(manifestLabel), "type"),
    summarizeList(
      resolveNames(search.real_asset_deployment_public_ids),
      "deployment"
    ),
    summarizeList(resolveNames(search.data_set_public_ids), "data set"),
    search.query
      ? `path "${search.query}"${search.regex ? " (regex)" : ""}`
      : null,
    summarizeList(search.file_extensions ?? [], "extension"),
    (search.data_asset_public_ids?.length ?? 0) > 0
      ? `${search.data_asset_public_ids?.length.toLocaleString()} specific asset${
          search.data_asset_public_ids?.length === 1 ? "" : "s"
        }`
      : null,
  ];

  const filtered = parts.filter((part): part is string => part !== null);
  return filtered.length > 0 ? filtered : ["Tier change"];
};

/** Copyable, tooltip'd job ID — kept available for API calls, never primary. */
export function CopyableJobId({ publicId }: { publicId: string }) {
  if (!publicId) {
    return null;
  }
  return (
    <button
      type="button"
      title={publicId}
      className="inline-flex items-center gap-1 text-xs font-normal text-grey-200 hover:text-blue-200"
      onClick={() => {
        void navigator.clipboard.writeText(publicId);
        toast.success("Job ID copied.");
      }}
    >
      <FaRegCopy size={11} />
      job ID
    </button>
  );
}

/**
 * Human-readable summary of a tiering job derived from its params:
 * target tier plus scope (manifests/deployments/data sets by display name,
 * path query, file extensions, specific-asset count).
 */
function JobSummary({
  params,
}: {
  params: Partial<ChangeStorageTierParams> | null | undefined;
}) {
  const nameByPublicId = useEntityNames(params?.search ?? {});
  return <span>{buildSummaryParts(params, nameByPublicId).join(" · ")}</span>;
}

export default JobSummary;
